// Centralized DOM lookups.
//
// `requireEls()` guards us from typo-style breakage: if any required element
// is missing we throw at startup rather than silently rendering a half-dead UI.

const $ = (id) => document.getElementById(id);

export const els = {
  statusDot: $("status-dot"),
  statusLine: $("status-line"),
  layoutRoot: $("layout-root"),
  workflowSel: $("workflow"),
  voiceSel: $("voice"),
  thinkingLevelSel: $("thinkingLevel"),
  videoSources: $("video-sources"),
  addVideoSourceBtn: $("add-video-source-btn"),
  captureAudioBtn: $("capture-audio-btn"),
  connectBtn: $("connect-btn"),
  micBtn: $("mic-btn"),
  // Frontend-only playback mute. Optional — the silent-mode feature degrades
  // to "always play" if this button is absent.
  silentModeBtn: $("silent-mode-btn"),
  captureCanvas: $("capture-canvas"),
  prevStepIndicator: $("prev-step-indicator"),
  nextStepIndicator: $("next-step-indicator"),
  workflowTitle: $("workflow-title"),
  currentStepCard: $("current-step-card"),
  stepMotion: $("step-motion"),
  currentStepCounter: $("current-step-counter"),
  currentStepTitle: $("current-step-title"),
  currentStepInstruction: $("current-step-instruction"),
  currentStepText: $("current-step-text"),
  currentStepImageShell: $("current-step-image-shell"),
  currentStepImage: $("current-step-image"),
  nextStepPreview: $("next-step-preview"),
  nextStepContent: $("next-step-content"),
  nextStepEyebrow: $("next-step-eyebrow"),
  nextStepTitle: $("next-step-title"),
  nextStepInstruction: $("next-step-instruction"),
  interruptNotice: $("interrupt-notice"),
  interruptNoticeText: $("interrupt-notice-text"),
  chatLog: $("chat-log"),
  chatForm: $("chat-form"),
  chatInput: $("chat-input"),
  chatSend: $("chat-send"),
  includeThoughts: $("includeThoughts"),
  debugPanel: $("debug-panel"),
  debugToggle: $("debug-toggle"),
  debugLog: $("debug-log"),
  debugClear: $("debug-clear"),
  debugMode: $("debug-mode"),
  debugMedia: $("debug-media"),
  debugAutoscroll: $("debug-autoscroll"),
  dbgState: $("dbg-state"),
  dbgWs: $("dbg-ws"),
  dbgAudioIn: $("dbg-audio-in"),
  dbgImages: $("dbg-images"),
  dbgAudioOut: $("dbg-audio-out"),
  dbgErrors: $("dbg-errors"),
  // Live "what the model sees" previews. Optional — the debug helpers no-op
  // when these are absent, so the existing DOM-required test surface and the
  // minimal test DOMs stay valid.
  debugViewSection: $("debug-view-section"),
  debugCurrentView: $("debug-current-view"),
  debugCurrentViewEmpty: $("debug-current-view-empty"),
  debugCompositeViewWrap: $("debug-composite-view-wrap"),
  debugCompositeView: $("debug-composite-view"),
  // Auth-related elements. The login overlay covers the app while
  // unauthenticated; the logout button is shown next to the workflow picker.
  loginOverlay: $("login-overlay"),
  loginForm: $("login-form"),
  loginSpace: $("login-space"),
  loginCode: $("login-code"),
  loginSubmit: $("login-submit"),
  loginError: $("login-error"),
  authStatus: $("auth-status"),
  authSpace: $("auth-space"),
  logoutBtn: $("logout-btn"),
  // AI-assisted image processing notice (EU AI Act transparency). Optional —
  // degrades to "always acknowledged" if the overlay markup is absent, which
  // keeps the existing minimal test DOMs valid.
  aiNoticeOverlay: $("ai-notice-overlay"),
  aiNoticeTitle: $("ai-notice-title"),
  aiNoticeBody: $("ai-notice-body"),
  aiNoticeAckBtn: $("ai-notice-ack-btn"),
  // Local-only recorder UI — all optional. The recorder degrades to a
  // no-op if any of these are missing, so we deliberately do NOT add them
  // to REQUIRED_KEYS (keeps the existing tests' permissive null-proxy DOM
  // mocks valid).
  recorderCard: $("recorder-card"),
  recorderBadge: $("recorder-badge"),
  recorderArmBtn: $("recorder-arm-btn"),
  recorderFreeBtn: $("recorder-free-btn"),
  recorderStatus: $("recorder-status"),
  // Packing-proof panel. Optional — only workflows with `packing_proof`
  // render into it, and the panel no-ops when the element is absent.
  packingPanel: $("packing-panel"),
  // Language switcher. Optional — the language module degrades to English
  // when these are absent, which keeps the existing DOM-required test
  // surface untouched.
  languageSwitcher: $("language-switcher"),
  languageSwitcherLabel: $("language-switcher-label"),
  languageSelect: $("language-select"),
};

const REQUIRED_KEYS = [
  "statusDot",
  "statusLine",
  "layoutRoot",
  "workflowSel",
  "voiceSel",
  "thinkingLevelSel",
  "videoSources",
  "addVideoSourceBtn",
  "captureAudioBtn",
  "connectBtn",
  "micBtn",
  "captureCanvas",
  "currentStepCard",
  "currentStepTitle",
  "currentStepInstruction",
  "chatLog",
];

export function ensureRequired() {
  const missing = REQUIRED_KEYS.filter((k) => !els[k]);
  if (missing.length) {
    throw new Error(`missing required DOM ids: ${missing.join(", ")}`);
  }
}
